import router from '@/router'
import defaultAvatar from '@/assets/images/location/location.svg'
import store from '@/store'
import MapPinRed from '@/assets/images/location/map-marker-icon-red.png'
import MapPinBlue from '@/assets/images/location/map-marker-icon-blue.png'
import MapPinOrange from '@/assets/images/location/map-marker-icon-orange.png'
import MapPinApprox from '@/assets/images/location/map-marker-icon-approx.png'
import MarkerShadow from 'leaflet/dist/images/marker-shadow.png'

import { icon } from 'leaflet'
import { useServicesQueryHandler } from '@/views/apps/service/usePoints'

export function useLocationOwnership() {
  /**
   * Normalize any ID to string for safe comparison.
   * @param {any} id
   * @returns {string|null}
   */
  const normalizeId = id => (id !== null && id !== undefined ? String(id) : null)

  /**
   * Extract owner ID from request/location-like objects.
   * Supports:
   * - request.address.user_id
   * - location.user_id
   * - request.location.user_id
   * - request.tiedLocation.user_id
   */
  const getLocationOwnerId = input => normalizeId(
    input?.address?.user_id
      ?? input?.location?.user_id
      ?? input?.tiedLocation?.user_id
      ?? input?.tied_location?.user_id
      ?? input?.owner_id
      ?? input?.user?.id
      ?? input?.user_id,
  )

  /**
   * Extract address owner ID safely.
   * Kept for compatibility with old request logic.
   */
  const getAddressUserId = request => normalizeId(request?.address?.user_id)

  /**
   * Checks if the request creator is the owner of the address/location.
   */
  const isOwner = request => {
    const requestUserId = normalizeId(request?.user_id)
    const ownerId = getAddressUserId(request) || getLocationOwnerId(request)

    return !!requestUserId && requestUserId === ownerId
  }

  /**
   * Checks if the request is supervised.
   */
  const isSupervised = request => {
    const address = request?.address
    const requestUserId = normalizeId(request?.user_id)
    if (!address || !requestUserId) return false

    const isAgencyManaged = address.service_request_policy === 'agency_managed'

    return isAgencyManaged || !isOwner(request)
  }

  /**
   * Checks if current user is the location owner.
   */
  const isCurrentUserLocationOwner = location => {
    const currentUserId = normalizeId(store.getters['account/user']?.id)
    const ownerId = getLocationOwnerId(location)

    return !!currentUserId && currentUserId === ownerId
  }

  return {
    isOwner,
    isSupervised,
    isCurrentUserLocationOwner,
    getLocationOwnerId,
  }
}
export const useLocationOptions = () => {
  const initLocationData = {
    id: '',
    user_id: '',
    // ✅ use nulls (not '') to avoid clobbering numeric guards
    lng: null,
    lat: null,
    service_request_policy: '',
    exact: true,
    address: {
      unit_id: '',
      country: '',
      country_code: '',
      postcode: '',
      municipality: '',
      county: '',
      state: '',
      locality: '',
      locality_type: '',
      state_district: '',
      suburb: '',
      district: '',
      quarter: '',
      borough: '',
      city_district: '',
      neighbourhood: '',
      man_made: '',
      road: '',
      house_number: '',
      building: '',
      apartment_number: '',
      address_confirmed: false,
      address_comment: '',
    },
    formatted_address: '',
    position: {
      lat: null,
      lng: null,
    },
    radius: 50000,
    viewport: {
      _southWest: {
        lat: 0,
        lng: 0,
      },
      _northEast: {
        lat: 0,
        lng: 0,
      },
    },
    viewportRadius: 0,
    viewportPosition: {
      lat: null,
      lng: null,
    },
    viewportBounds: {},
  }

  const getMarker = style => {
    switch (style) {
      case 'red':
        return MapPinRed
      case 'orange':
        return MapPinOrange
      case 'approx':
        return MapPinApprox
      default:
        return MapPinBlue
    }
  }

  const getMarkerIconOptions = style => {
    const iconUrl = getMarker(style)
    const PIN_SIZE = [40, 60]
    const APPROX_SIZE = [128, 128]
    return style !== 'approx'
      ? {
        iconRetinaUrl: iconUrl,
        iconUrl,
        shadowUrl: MarkerShadow,
        iconSize: PIN_SIZE,
        iconAnchor: [PIN_SIZE[0] / 2, PIN_SIZE[1]],
        popupAnchor: [1, -34],
        tooltipAnchor: [16, -28],
        shadowSize: [64, 64],
      }
      : {
        iconRetinaUrl: iconUrl,
        iconUrl,
        shadowUrl: MarkerShadow,
        iconSize: APPROX_SIZE,
        iconAnchor: [APPROX_SIZE[0] / 2, APPROX_SIZE[1] / 2],

        popupAnchor: [1, -34],
        tooltipAnchor: [16, -28],
        shadowSize: [0, 0],
      }
  }

  const getMarkerIcon = style => icon(getMarkerIconOptions(style))

  return {
    initLocationData,
    getMarkerIconOptions,
    getMarkerIcon,
  }
}

export const useLocationUi = () => {
  const {
    getLinkToQueryPage,
    SERVICE_LOCATION_REQUEST_KEY,
  } = useServicesQueryHandler()

  const getLocationRequestRoute = location => {
    if (!location || !location.public_alias) {
      return { name: 'app-service-query' }
    }

    // This is the SPA route – will hit /q and your handler
    return {
      name: 'app-service-query',
      query: {
        [SERVICE_LOCATION_REQUEST_KEY]: String(location.public_alias),
      },
    }
  }

  const getLocationRequestLink = location => {
    if (!location || !location.public_alias) return ''

    // Base already includes ?lang=...
    const base = getLinkToQueryPage()
    const url = new URL(base)

    // Add / override slr
    url.searchParams.set(SERVICE_LOCATION_REQUEST_KEY, String(location.public_alias))

    return url.href // e.g. https://host/q?lang=ru&slr=ALIAS123
  }

  const locationAddRequest = location => {
    router.push(getLocationRequestRoute(location))
  }

  const locationEdit = location => {
    router.push({ name: 'apps-location-edit', params: { id: location.id } })
  }

  const getAvatar = location => location.avatar || defaultAvatar

  const locationInfo = location => {
    router.push({ name: 'apps-location-info', params: { id: location.id } })
  }

  const getPolicyMeta = input => {
    const policy = typeof input === 'string'
      ? input
      : input?.service_request_policy

    if (policy === 'agency_managed') {
      return {
        value: 'agency_managed',
        icon: 'ShieldIcon',
        variant: 'light-primary',
        textClass: 'text-primary',
        labelKey: 'location.policy.agency_managed',
        hintKey: 'location.policy.agency_managed_for_resident_hint',
      }
    }

    if (policy === 'resident_managed') {
      return {
        value: 'resident_managed',
        icon: 'UserIcon',
        variant: 'light-warning',
        textClass: 'text-warning',
        labelKey: 'location.policy.resident_managed',
        hintKey: 'location.policy.resident_managed_for_resident_hint',
      }
    }

    return {
      value: null,
      icon: 'HelpCircleIcon',
      variant: 'light-secondary',
      textClass: 'text-secondary',
      labelKey: '',
      hintKey: '',
    }
  }

  const isAgencyManaged = location => location?.service_request_policy === 'agency_managed'

  const isResidentManaged = location => location?.service_request_policy === 'resident_managed'

  const hasUnitId = location => {
    const v = location?.address?.unit_id
    return !!(v && String(v).trim())
  }

  return {
    locationInfo,
    locationEdit,
    locationAddRequest,
    getAvatar,
    getLocationRequestLink,
    getPolicyMeta,
    isAgencyManaged,
    isResidentManaged,
    hasUnitId,
  }
}

export const useLocationRemoteData = () => {
  const normalizeAddressPartForCompare = value => String(value || '')
    .trim()
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,;:()[\]{}'"`’‘“”\-_/\\]+/g, ' ')
    .replace(/\s+/g, ' ')

  const isSameAddressPart = (a, b) => {
    const left = normalizeAddressPartForCompare(a)
    const right = normalizeAddressPartForCompare(b)

    return !!left && !!right && left === right
  }

  const uniqueAddressParts = parts => {
    const used = new Set()

    return parts
      .map(part => String(part || '').trim())
      .filter(Boolean)
      .filter(part => {
        const key = normalizeAddressPartForCompare(part)

        if (!key || used.has(key)) return false

        used.add(key)

        return true
      })
  }

  const firstAddressPart = (...parts) => parts.find(part => String(part || '').trim()) || ''

  const getLocalityType = location => {
    const address = location?.address || {}

    if ('city' in address) return 'city'
    if ('town' in address) return 'town'
    if ('village' in address) return 'village'

    return ''
  }

  const transformFetchedLocality = location => {
    const address = location?.address || {}

    return firstAddressPart(
      address.locality,
      address.city,
      address.town,
      address.village,
      address.municipality,
      address.suburb,
    )
  }

  const getRegionAddressPart = (address, locality = '') => {
    const candidates = [
      address.province,
      address.state,
      address.state_district,
      address.county,
      address.region,
    ]

    return candidates.find(part => {
      if (!part) return false

      return !isSameAddressPart(part, locality)
    }) || ''
  }

  const formatShortAddress = (input, options = {}) => {
    const {
      uppercaseCountry = true,
      includeDistrictFallback = false,
    } = options

    const address = input?.address || input || {}

    if (!address || typeof address !== 'object') return ''

    const country = firstAddressPart(address.country)
    const locality = transformFetchedLocality({ address })

    let region = getRegionAddressPart(address, locality)

    if (!region && includeDistrictFallback) {
      region = [
        address.borough,
        address.city_district,
        address.district,
        address.suburb,
      ].find(part => part && !isSameAddressPart(part, locality)) || ''
    }

    return uniqueAddressParts([
      country && uppercaseCountry ? country.toUpperCase() : country,
      region,
      locality,
    ]).join(', ')
  }

  const getLocationPayload = () => {
    const { initLocationData } = useLocationOptions()
    const location = store.getters['location/location']
    const locationAddress = JSON.parse(
      JSON.stringify({ ...initLocationData.address, ...location.address }),
    )

    const data = { lat: location.lat, lng: location.lng }
    const address = {
      unit_id: locationAddress.unit_id,
      country: locationAddress.country,
      country_code: locationAddress.country_code,
      postcode: locationAddress.postcode,
      municipality: locationAddress.municipality,
      county: locationAddress.county,
      state: locationAddress.state,
      province: locationAddress.province,
      locality: locationAddress.locality,
      locality_type: getLocalityType(location),
      state_district: locationAddress.state_district,
      suburb: locationAddress.suburb,
      district: locationAddress.district,
      quarter: locationAddress.quarter,
      borough: locationAddress.borough,
      city_district: locationAddress.city_district,
      neighbourhood: locationAddress.neighbourhood,
      man_made: locationAddress.man_made,
      road: locationAddress.road,
      house_number: locationAddress.house_number,
      building: locationAddress.building,
      apartment_number: locationAddress.apartment_number,
      address_comment: locationAddress.address_comment,
      address_confirmed: location.address_confirmed,
    }

    return { data, address }
  }

  const addLocationLookup = () => store.dispatch('location/addLocationLookup', getLocationPayload())

  return {
    addLocationLookup,
    getLocationPayload,
    transformFetchedLocality,
    formatShortAddress,
    uniqueAddressParts,
    isSameAddressPart,
  }
}
